import { Clock, Lock, UserRound } from "@tamagui/lucide-icons";
import { Image } from "expo-image";
import { View, XStack, YStack } from "tamagui";

import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import SurescriptsLogo from "@/assets/images/surescripts-logo.png";
import {
  Button,
  Footer,
  Heading,
  Link,
  Logo,
  Paragraph,
  ScrollView,
} from "@/components";

const SurescriptsCuraiLogo = () => (
  <XStack alignItems="center" gap="$150">
    <Logo height="$350" $lg={{ height: "$375" }} />
    <View width={1} height="$300" backgroundColor="$color" opacity={0.25} />
    <Image
      source={SurescriptsLogo}
      style={{ height: 30, width: 100, marginBottom: 4 }}
      contentFit="contain"
      accessibilityLabel="Surescripts"
    />
  </XStack>
);

const BENEFITS = [
  { Icon: Clock, text: "Save 10 minutes on your visit time" },
  { Icon: UserRound, text: "Your clinician can see your health history" },
  { Icon: Lock, text: "HIPAA compliant and SOC2 certified" },
] as const;

export const SurescriptsConsentScreen = ({
  onContinue,
}: {
  onContinue: () => void;
}) => {
  const { mutate: updateConsents, isPending } = useUpdateConsents();

  const handleConsent = () => {
    updateConsents(
      { data: { consents: { [ConsentType.surescripts_consent]: true } } },
      { onSuccess: () => onContinue() },
    );
  };

  return (
    <>
      <XStack
        backgroundColor="$fillClinician"
        paddingVertical="$500"
        marginHorizontal="$200"
        borderRadius="$200"
        justifyContent="center"
        alignItems="center"
      >
        <SurescriptsCuraiLogo />
      </XStack>

      <ScrollView
        flex={1}
        contentContainerStyle={{
          padding: "$200",
          gap: "$250",
        }}
      >
        <YStack gap="$100">
          <Heading size="$lg">
            Share your medical history for faster and personalized care
          </Heading>
          <Paragraph size="$sm" color="$textSecondary">
            Curai Health uses{" "}
            <Link
              size="$sm"
              external
              semibold
              href="https://www.surescripts.com/"
            >
              Surescripts
            </Link>{" "}
            to share medical history with your clinician.
          </Paragraph>
        </YStack>

        <YStack gap="$200">
          {BENEFITS.map(({ Icon, text }) => (
            <XStack key={text} gap="$150" alignItems="center">
              <Icon size="$300" color="$iconSecondary" flexShrink={0} />
              <Paragraph size="$sm">{text}</Paragraph>
            </XStack>
          ))}
        </YStack>
      </ScrollView>

      <Footer padding="$200" mode="dialog">
        <Button
          dataTrackingId="surescripts-consent-confirm-button"
          mode="primary"
          constrained
          onPress={handleConsent}
          disabled={isPending}
          loading={isPending}
        >
          Consent to share & continue
        </Button>
        <Button
          dataTrackingId="surescripts-consent-skip-button"
          mode="tertiary"
          onPress={onContinue}
          disabled={isPending}
        >
          Continue without sharing
        </Button>
      </Footer>
    </>
  );
};
